'use client';

import type { Balance, IWallet } from '@fanduel/cpe-um-bundle/wallet/contract';
import type { IWalletOwner } from '@fanduel/cpe-um-bundle/wallet/library';
import type { CasinoBalance } from '@/types/user-context/wallet';
import { useEffect, useState } from 'react';
import { PRODUCT, REGIONS, TIMERS_IN_MS, UNIFIED_MODULES_ROUTES } from '@/utils/constants';
import { useRouter } from 'next/navigation';
import { useAccountUm } from '@/hooks/use-account-um';
import { useStore } from '@/store/store';
import { getConfig } from '@/utils/config/config';
import { useGlobalContext } from '@/store/contexts/global-context';

export const WALLET_REFRESH_TIME = TIMERS_IN_MS.ONE_MINUTE / 2;

let initialized: boolean = false;
let initializationPromise: Promise<void> | null = null;

let walletManager: (IWalletOwner & IWallet) | null = null;

const normalizeWalletResponse = (walletBalance: Set<Balance>): CasinoBalance => {
    const details: Record<string, number> = Array.from(walletBalance).reduce(
        (accumulator, { accountType, balance }) => {
            return { ...accumulator, [accountType]: balance };
        },
        {}
    );

    return {
        playableBalance: process.env.PRODUCT === PRODUCT.POKER ? details['POKER_PLAYABLE'] : details['CASINO_PLAYABLE'],
        casinoDeposits: details['USER_SPORTSBOOK_DEPOSIT'],
        casinoBonuses: details['USER_CASINO_BONUS'],
        freeBets: details['USER_FREE_BETS'],
        sharedWinnings: details['SHARED_WINNINGS']
    };
};

export const useWalletUm = () => {
    const [balance, setBalance] = useState<CasinoBalance>();

    const router = useRouter();
    const { isAndroidWrapper, region } = useGlobalContext();
    const { isSessionValid, getSession } = useAccountUm();
    const depositModal = useStore((state) => state.accountHubModals.deposit);

    const triggerDeposit = async () => {
        if (isAndroidWrapper) {
            const rootDomain = getConfig()?.rootDomain;
            window.location.href = `https://account.${region.toLowerCase() || REGIONS.MI.toLowerCase()}.${rootDomain}/account/deposit`;
        } else {
            depositModal.setIsOpen(true);
            try {
                await walletManager?.depositFundsAsync();
            } finally {
                depositModal.setIsOpen(false);
            }
        }
    };

    const triggerWithdrawls = async () => {
        if (isAndroidWrapper) {
            router.push(UNIFIED_MODULES_ROUTES.WITHDRAWALS);
        } else {
            walletManager?.withdrawFundsAsync();
        }
    };

    const getWalletBalance = async () => {
        if (initializationPromise) {
            await initializationPromise;
        }

        const balances = await walletManager?.getBalancesAsync();

        // NOT getting balances can mean that the session is not valid, so we check it explicitly to be sure
        if (!balances) {
            await isSessionValid();
        }

        if (balances && balances.size > 0) {
            const normalized = normalizeWalletResponse(balances!);
            setBalance(normalized);
            useStore.getState().userContext.setBalance(normalized.playableBalance ?? 0);
        }
    };

    // Initializer
    const initializeWalletUM = async () => {
        if (initialized) {
            return;
        }

        if (initializationPromise) {
            await initializationPromise;
            return;
        }

        initializationPromise = (async () => {
            walletManager = (await import('@fanduel/cpe-um-bundle/wallet/library')).Wallet;

            const { initializeUnifiedModules } = await import('@/logic/unified-modules');
            await initializeUnifiedModules();

            const handleFocus = async () => {
                const currentIsLoggedIn = useStore.getState().userContext.isLoggedIn;
                if (document.visibilityState === 'visible' && currentIsLoggedIn) {
                    const res = await walletManager?.getBalancesAsync();
                    if (!res) {
                        await isSessionValid();
                    }
                }
            };

            //TODO add cleanup function ...
            document.addEventListener('visibilitychange', handleFocus);

            initialized = true;
        })();

        await initializationPromise;

        const session = await getSession();
        if (session) {
            getWalletBalance();
        }
    };

    useEffect(() => {
        initializeWalletUM();
    }, []);

    return {
        balance,
        getWalletBalance,
        triggerDeposit,
        triggerWithdrawls
    };
};
